// `/account`, WITH THE SERVER'S WEB_ACCOUNTS_ENABLED ON: "Create your account" (ACC-3a; ACCOUNTS_PLAN §3 A0–A12).
//
// THE ORDER IS THE PLAN'S. The heading and what it is for (A1, A2); the standing disclosure (A7) and the terms box (A8,
// WEB-COPY-1's approved checkbox sentence with its two links inside it, as the assent page has them); the "news and
// offers" box (A9, unticked); then "Continue with Apple" (A0), the first button, per Apple's guidelines; then "or use your
// email" (A3), the email field with A5 or A6 under it (A6 when the box is ticked), the password with Show/Hide (A4), and
// "Create account and continue" (A10); then "Already have an account? Sign in" (A11) and K7's small print (A12).
//
// THE TERMS BOX COMES FIRST FOR BOTH WAYS IN. The plan: "With Apple, the terms box and the marketing box still show,
// above the Apple button, and must be ticked first." So both boxes sit above the Apple button, and until the terms box is
// ticked neither button can be pressed; the app's own hint, "Tick the box above to continue" (OnboardingView.swift:628),
// sits under the box, as the assent page shows it. `test/accountScreens.test.tsx` presses the Apple button before the tick
// and finds no Apple window opened (mutation probe 2).
//
// CREATING LINKS, NEVER STARTS OVER. The seam links this browser's look-around to the new account, so the uid — and the
// appeal this tab is building — carry over (`services/auth.ts` `createAccountWithPassword`, ACCOUNTS_PLAN §2.3). Apple
// links too; an Apple ID that is already an account (the app's usual case) signs in to it instead, and the look-around
// is left behind (`finish.ts`).
//
// WHAT IT RECORDS. The terms box is the same act the assent page records: `v2Credits` `assent`, citing the hashes of the
// exact Terms and Privacy Policy served (`legal` on `v2Credits` `get`), under the account. A password account is sent
// its confirmation email (E2) at once, since it must confirm before paying (Tyler's decision 2) and `/checkout` says "We
// sent a link to {email}" (C7). The "news and offers" answer (A9) is kept in the session with its text hash: no server
// op records one yet (ACC-1 built none, EM-1 is not built), which is the gap the ACC-3a report names.

import { useId, useState } from "react";
import { useLocation } from "react-router-dom";
import type { V2Failure } from "@contract";
import type { AuthRefusal } from "../../services";
import { Busy } from "../../app/accounts";
import { useSession } from "../../app/session";
import { useCall } from "../../app/useCall";
import { heldText } from "../../components/heldCopy";
import { prePayText } from "../../components/prePayCopy";
import { Refusal } from "../../components/Refusal";
import { PRIVACY_PHRASE, sentenceWithLinks, TERMS_PHRASE } from "../../components/legalLinks";
import { APP_VERSION } from "../assent";
import { checkboxSha256 } from "../../app/shownCopy";
import { accountState, useFinishAccountStep, useUnpaidLookAround } from "./finish";
import {
  AccountForm, AppleButton, AuthError, Divider, EmailField, HeroTile, PasswordField, Say, SayWithLink, SubmitButton,
} from "./parts";
import { looksLikeEmail } from "./refusalCopy";
import { textSha256 } from "../../app/consentHash";

export function CreateAccount() {
  const session = useSession();
  const location = useLocation();
  const finish = useFinishAccountStep();
  const unpaidLookAround = useUnpaidLookAround();
  const arrived = accountState(location.state);
  const to = arrived.from ?? "/checkout";

  // Not on `session.revision`: the terms this page shows are the ones the owner ticks, and a refresh mid-step must not
  // swap them.
  const state = useCall(() => session.transport.v2Credits.get(), [session.transport]);

  const [agreed, setAgreed] = useState(false);
  const [news, setNews] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [refusal, setRefusal] = useState<AuthRefusal | null>(null);
  const [working, setWorking] = useState<"apple" | "password" | null>(null);
  const [assentFailure, setAssentFailure] = useState<V2Failure | null>(null);
  // The account made on this page, when recording the assent then failed: pressing the button again retries the record,
  // never a second account.
  const [made, setMade] = useState<Parameters<typeof finish>[0] | null>(null);
  const hintId = useId();
  const helpId = useId();
  const headingId = useId();

  if (state.status === "pending") return <Busy />;
  if (state.status === "failed") return <Refusal failure={state.failure} context="The terms" />;
  const legal = state.response.body.legal;

  /** The terms act, the tick, and on. */
  const complete = async (result: Parameters<typeof finish>[0]) => {
    const recorded = await session.transport.v2Credits.assent({
      kind: "onboardingTerms",
      termsSha256: legal.terms.sha256,
      privacySha256: legal.privacy.sha256,
      termsVersion: legal.terms.version,
      privacyVersion: legal.privacy.version,
      clientTimestampMs: Date.now(),
      appVersion: APP_VERSION,
      ...(await checkboxSha256()),
    });
    if (!recorded.ok) {
      setMade(result);
      setAssentFailure(recorded);
      return;
    }
    const tick = {
      ticked: news,
      copyId: "account.create.marketing",
      textSha256: await textSha256(heldText("account.create.marketing")),
      atMs: Date.now(),
    } as const;
    session.setMarketingTick(tick);
    // F-ACC3a-1, CLOSED BY ACC-3b: the choice is RECORDED, through EM-1's one store (`contactSet` on the `web-account`
    // surface), against the hash of the words shown: the box (A9), ticked or not, since an untick is an answer too.
    // R189 (B4): THE LINE UNDER THE EMAIL FIELD IS NO LONGER A REMINDERS CONSENT, so nothing records it. Its launch words
    // (A5, A6, `LAUNCH_NO_REMINDERS`) promise no reminder, because none is sent until EM-3 exists, and a consent record
    // must cite words that ask for that consent. The server issues only the ORIGINAL A5/A6 as reminders wording
    // (`REMINDER_ORIGINALS`), so a tick citing the new words would be refused 400 — and take the box's record down with
    // it, since one call records both or neither. EM-3 brings the reminders line's words and its record back TOGETHER —
    // not `WEBSITE_REMINDERS_LIVE` alone, which would record the launch words as a consent they do not ask for.
    // A record that fails, or a server with EMAIL_CONTACT_ENABLED off, does not stop the sign-up: it is the owner's
    // account either way, and Settings shows and changes what is on file.
    if (tick.textSha256 !== null) {
      await session.transport.v2Credits.contactSet({
        surface: "web-account",
        clientTimestampMs: tick.atMs,
        marketing: { on: tick.ticked, textSha256: tick.textSha256 },
      });
    }
    await finish(result, to, unpaidLookAround);
  };

  const run = async (how: "apple" | "password") => {
    if (!agreed || working !== null) return;
    setRefusal(null);
    setAssentFailure(null);
    setWorking(how);
    try {
      if (made !== null) {
        await complete(made);
        return;
      }
      if (how === "password") {
        if (!looksLikeEmail(email)) return setRefusal({ kind: "notAnEmail" });
        if (password.length < 8) return setRefusal({ kind: "passwordTooShort" });
      }
      const result = how === "apple"
        ? await session.auth.continueWithApple()
        : await session.auth.createAccountWithPassword(email.trim(), password);
      if (!result.ok) return setRefusal(result.refusal);
      // E2 for a password account, now: it must confirm before paying, and `/checkout` says the link was sent. A send
      // that fails leaves C7's "Send it again" to try again; it does not undo the account.
      if (result.provider === "password") await session.auth.sendEmailVerification();
      await complete(result);
    } finally {
      setWorking(null);
    }
  };

  return (
    <section className="account-page" aria-labelledby={headingId} data-account-screen="create">
      <HeroTile />
      <h1 id={headingId} data-held-copy="account.create.heading">{heldText("account.create.heading")}</h1>
      <Say id="account.create.tied" className="account-lead" />

      {arrived.accountRequired && (
        <div className="note warn" data-held-copy="account.error.accountRequired">{heldText("account.error.accountRequired")}</div>
      )}

      <AccountForm onSubmit={() => void run("password")}>
        <div className="account-card">
          {/* A7: WEB-COPY-1 item 2, the app's onboarding summary. */}
          <Say id="assent.standingDisclosure" className="muted account-disclosure" />
          <label className="toggle account-toggle">
            <input type="checkbox" name="terms" checked={agreed} onChange={(e) => setAgreed(e.currentTarget.checked)} />
            {/* A8: WEB-COPY-1 item 1, pending counsel (R19), with its two links inside it. */}
            <span className="consent" data-held-copy="assent.onboardingCheckbox">
              {sentenceWithLinks(heldText("assent.onboardingCheckbox"), [
                { phrase: TERMS_PHRASE, doc: legal.terms },
                { phrase: PRIVACY_PHRASE, doc: legal.privacy },
              ])}
            </span>
          </label>
          {!agreed && (
            <p id={hintId} className="faint tick-first" data-app-copy="assent.tickFirst">
              {prePayText("assent.tickFirst")}
            </p>
          )}
          <label className="toggle account-toggle">
            <input type="checkbox" name="news" checked={news} onChange={(e) => setNews(e.currentTarget.checked)} />
            <span className="consent-plain" data-held-copy="account.create.marketing">{heldText("account.create.marketing")}</span>
          </label>
        </div>

        <AppleButton
          copyId="account.create.apple"
          disabled={!agreed || (working !== null && working !== "apple")}
          busy={working === "apple"}
          describedBy={agreed ? undefined : hintId}
          onClick={() => void run("apple")}
        />

        <Divider />

        <EmailField value={email} onChange={setEmail} describedBy={helpId} invalid={refusal?.kind === "notAnEmail"} />
        <p id={helpId} className="field-foot account-help" data-held-copy={news ? "account.create.emailHelpWithNews" : "account.create.emailHelp"}>
          {heldText(news ? "account.create.emailHelpWithNews" : "account.create.emailHelp")}
        </p>
        <PasswordField value={password} onChange={setPassword} autoComplete="new-password" hint invalid={refusal?.kind === "passwordTooShort"} />

        <AuthError refusal={refusal} />
        {assentFailure !== null && <Refusal failure={assentFailure} context="Recording your agreement" />}

        <SubmitButton
          id="account.create.button"
          busy={working === "password"}
          disabled={!agreed || (working !== null && working !== "password")}
          describedBy={agreed ? undefined : hintId}
        />
      </AccountForm>

      <SayWithLink id="account.create.haveAccount" to="/signin" state={{ from: arrived.from }} className="account-switch" />
      <Say id="account.create.appNote" className="faint account-small" />
    </section>
  );
}
